import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close,
  School,
  Work,
  WorkspacePremium,
  Badge as BadgeIcon,
  Person,
} from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { filterFieldSx } from "../theme/filterStyles";
import {
  professionalCurriculumService,
  CurriculumItem,
  CurriculumTypeSlice,
  ProfessionalCurriculumRow,
} from "../services/professionalCurriculumService";

// Currículo dos profissionais (RN-213): quem sabe o quê nesta clínica. Só leitura — cada
// profissional é dono do próprio currículo e o mantém em Meu perfil.

/** A ordem aqui é a ordem em que o currículo é lido no card, não a alfabética. */
const KINDS = [
  { key: "formacao", label: "Formação", plural: "Formações", icon: <School fontSize="small" />, color: "#0d6e6e" },
  { key: "experiencia", label: "Experiência", plural: "Experiências", icon: <Work fontSize="small" />, color: "#6610f2" },
  { key: "certificacao", label: "Certificação", plural: "Certificações", icon: <WorkspacePremium fontSize="small" />, color: "#fd7e14" },
  { key: "registro", label: "Registro", plural: "Registros", icon: <BadgeIcon fontSize="small" />, color: "#0d6efd" },
];

const cardSx = {
  backgroundColor: "white",
  borderRadius: "12px",
  padding: "1rem 1.25rem",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  border: "1px solid #e9ecef",
};

const COLUNAS = [
  { titulo: "Profissional", flex: "1 1 220px", classe: "" },
  { titulo: "Especialidade", flex: "0 0 170px", classe: "" },
  { titulo: "Documento", flex: "0 0 130px", classe: "c4u-col--lg" },
  { titulo: "Formação", flex: "0 0 90px", classe: "c4u-col--md" },
  { titulo: "Experiência", flex: "0 0 95px", classe: "c4u-col--xl" },
  { titulo: "Certificações", flex: "0 0 105px", classe: "c4u-col--lg" },
  { titulo: "Itens", flex: "0 0 70px", classe: "" },
];

/** dd/mm/aaaa a partir de um ISO; o card mostra só mês/ano, que é como se lê currículo. */
const mesAno = (iso: string | null): string => {
  if (!iso) return "";
  const [y, m] = iso.slice(0, 10).split("-");
  return `${m}/${y}`;
};

const periodo = (item: CurriculumItem): string => {
  const inicio = mesAno(item.startedAt);
  const fim = item.current ? "atual" : mesAno(item.endedAt);
  if (!inicio && !fim) return "";
  if (!inicio) return fim;
  if (!fim) return inicio;
  return `${inicio} — ${fim}`;
};

/**
 * Foto do profissional. A imagem não tem URL pública (proxy autenticado do MinIO), então cada
 * avatar busca o próprio blob e o devolve ao desmontar — sem isso a lista vaza object URLs.
 */
const FotoProfissional: React.FC<{ userId: string | null; hasPhoto: boolean; nome: string; size: number }> = ({
  userId,
  hasPhoto,
  nome,
  size,
}) => {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!hasPhoto || !userId) return undefined;
    let ativo = true;
    let criada: string | null = null;
    professionalCurriculumService
      .photoUrl(userId, size <= 64)
      .then((u) => {
        criada = u;
        if (ativo) setUrl(u);
        else URL.revokeObjectURL(u);
      })
      .catch(() => undefined);
    return () => {
      ativo = false;
      if (criada) URL.revokeObjectURL(criada);
    };
  }, [userId, hasPhoto, size]);

  const iniciais = nome
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <Avatar
      src={url ?? undefined}
      sx={{ width: size, height: size, fontSize: size / 2.6, bgcolor: "#dee2e6", color: "#495057" }}
    >
      {url ? null : iniciais || <Person fontSize="small" />}
    </Avatar>
  );
};

/** O card: currículo inteiro de uma pessoa, agrupado por tipo. */
const CardProfissional: React.FC<{ row: ProfessionalCurriculumRow; onClose: () => void }> = ({ row, onClose }) => {
  const grupos = KINDS.map((k) => ({ ...k, itens: row.items.filter((i) => i.kind === k.key) })).filter(
    (g) => g.itens.length > 0,
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2.5,
          borderBottom: "1px solid #e9ecef",
          backgroundColor: "#f8f9fa",
        }}
      >
        <FotoProfissional userId={row.userId} hasPhoto={row.hasPhoto} nome={row.name} size={72} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.25, overflowWrap: "anywhere" }}>
            {row.name}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#6c757d" }}>
            {row.professionalTypeName || "Sem especialidade cadastrada"}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
            {row.documentProfessional && (
              <Chip size="small" label={row.documentProfessional} sx={{ fontSize: 11, height: 22 }} />
            )}
            {row.email && (
              <Chip size="small" variant="outlined" label={row.email} sx={{ fontSize: 11, height: 22 }} />
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Fechar">
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        {grupos.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: "#6c757d" }}>
            Este profissional ainda não preencheu o currículo em Meu perfil. O relatório não inventa
            dados — quem preenche é a própria pessoa.
          </Typography>
        ) : (
          grupos.map((g) => (
            <Box key={g.key} sx={{ mb: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Box sx={{ color: g.color, display: "flex" }}>{g.icon}</Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: g.color, textTransform: "uppercase", letterSpacing: 0.4 }}>
                  {g.itens.length === 1 ? g.label : g.plural}
                </Typography>
              </Box>
              {g.itens.map((item, idx) => (
                <Box
                  key={`${g.key}-${idx}`}
                  sx={{
                    borderLeft: `3px solid ${g.color}22`,
                    pl: 1.5,
                    py: 0.75,
                    mb: 0.5,
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, overflowWrap: "anywhere" }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#adb5bd", whiteSpace: "nowrap" }}>
                      {periodo(item)}
                    </Typography>
                  </Box>
                  {item.institution && (
                    <Typography sx={{ fontSize: 12.5, color: "#6c757d", overflowWrap: "anywhere" }}>
                      {item.institution}
                    </Typography>
                  )}
                  {item.description && (
                    <Typography sx={{ fontSize: 12.5, color: "#495057", mt: 0.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                      {item.description}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ))
        )}
      </DialogContent>
    </>
  );
};

const ReportProfessionalCurriculum: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();
  const theme = useTheme();
  // No celular o card ocupa a tela inteira: em 360px de largura um dialogo com margem
  // deixa o curriculo numa coluna estreita demais para ser lido.
  const cardEmTelaCheia = useMediaQuery(theme.breakpoints.down("sm"));

  const [search, setSearch] = useState("");
  const [typeId, setTypeId] = useState("");
  const [onlyWith, setOnlyWith] = useState("");

  const [items, setItems] = useState<ProfessionalCurriculumRow[]>([]);
  const [byType, setByType] = useState<CurriculumTypeSlice[]>([]);
  const [resumo, setResumo] = useState<{ professionals: number; withCurriculum: number; withoutCurriculum: number; totalEntries: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aberto, setAberto] = useState<ProfessionalCurriculumRow | null>(null);

  /**
   * As opções do filtro de especialidade saem da PRIMEIRA carga, sem filtro. Recalcular a cada
   * resposta esvaziaria o próprio combo: escolher "Fonoaudiologia" faria sumir todas as outras
   * opções, e não haveria como voltar.
   */
  const especialidades = useRef<{ id: string; label: string }[]>([]);
  const [opcoesProntas, setOpcoesProntas] = useState(false);

  const hasFilterChanges = !!search || !!typeId || !!onlyWith;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await professionalCurriculumService.get({
        search: search.trim() || undefined,
        professionalTypeId: typeId || undefined,
        onlyWithCurriculum: onlyWith === "com",
      });

      if (especialidades.current.length === 0 && !search && !typeId && !onlyWith) {
        const mapa = new Map<string, string>();
        data.items.forEach((i) => {
          if (i.professionalTypeId && i.professionalTypeName) mapa.set(i.professionalTypeId, i.professionalTypeName);
        });
        especialidades.current = Array.from(mapa, ([id, label]) => ({ id, label })).sort((a, b) =>
          a.label.localeCompare(b.label, "pt-BR"),
        );
        setOpcoesProntas(true);
      }

      // "Sem currículo" não tem filtro no servidor de propósito: é o avesso do que ele já
      // devolve, e resolver aqui evita um parâmetro a mais para uma pergunta só.
      setItems(onlyWith === "sem" ? data.items.filter((i) => i.total === 0) : data.items);
      setByType(data.byType);
      setResumo({
        professionals: data.professionals,
        withCurriculum: data.withCurriculum,
        withoutCurriculum: data.withoutCurriculum,
        totalEntries: data.totalEntries,
      });
    } catch {
      setError("Não foi possível carregar os currículos.");
      setItems([]);
      setByType([]);
      setResumo(null);
    } finally {
      setLoading(false);
    }
  }, [search, typeId, onlyWith]);

  useEffect(() => {
    load();
  }, [load]);

  const clearFilters = () => {
    setSearch("");
    setTypeId("");
    setOnlyWith("");
  };

  const cobertura = useMemo(
    () => (resumo && resumo.professionals > 0 ? Math.round((100 * resumo.withCurriculum) / resumo.professionals) : 0),
    [resumo],
  );

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f8f9fa" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Currículo dos profissionais
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Quem sabe o quê na clínica. Clique no profissional para ver o currículo completo.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Busca (nome, curso, instituição)"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 60 }}
                />
                <TextField
                  select
                  size="small"
                  label="Especialidade"
                  value={typeId}
                  onChange={(e) => setTypeId(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                  disabled={!opcoesProntas}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {especialidades.current.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Currículo"
                  value={onlyWith}
                  onChange={(e) => setOnlyWith(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="com">Com currículo</MenuItem>
                  <MenuItem value="sem">Sem currículo</MenuItem>
                </TextField>
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges || loading} />
              </div>
            </div>

            {resumo && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
                  gap: 2,
                  mb: 2,
                }}
              >
                {[
                  { label: "Profissionais", valor: resumo.professionals, cor: "#0d6e6e" },
                  { label: "Com currículo", valor: resumo.withCurriculum, cor: "#28a745", nota: `${cobertura}% da equipe` },
                  { label: "Sem currículo", valor: resumo.withoutCurriculum, cor: resumo.withoutCurriculum > 0 ? "#dc3545" : "#adb5bd", nota: "a cobrar" },
                  { label: "Itens registrados", valor: resumo.totalEntries, cor: "#6610f2" },
                ].map((k) => (
                  <Paper key={k.label} elevation={0} sx={cardSx}>
                    <Typography sx={{ fontSize: 12, color: "#6c757d" }}>{k.label}</Typography>
                    <Typography sx={{ fontSize: 26, fontWeight: 700, color: k.cor, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }}>
                      {k.valor}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#adb5bd" }}>{k.nota ?? " "}</Typography>
                  </Paper>
                ))}
              </Box>
            )}

            {byType.length > 0 && (
              <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1.25 }}>Por especialidade</Typography>
                {byType.map((s) => {
                  const largura = Math.round((100 * s.professionals) / Math.max(1, byType[0].professionals));
                  return (
                    <Box key={s.label} sx={{ mb: 1 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
                        <Typography sx={{ fontSize: 12.5, color: "#495057", overflowWrap: "anywhere" }}>{s.label}</Typography>
                        <Typography sx={{ fontSize: 12, color: "#6c757d", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                          {s.professionals} {s.professionals === 1 ? "profissional" : "profissionais"} · {s.entries} itens
                        </Typography>
                      </Box>
                      <Box sx={{ height: 5, borderRadius: 3, backgroundColor: "#f1f3f5", overflow: "hidden" }}>
                        <Box sx={{ width: `${largura}%`, height: "100%", backgroundColor: "#0d6e6e", transition: "width .3s" }} />
                      </Box>
                    </Box>
                  );
                })}
              </Paper>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} />
              </Box>
            ) : error ? (
              <Typography sx={{ p: 3, fontSize: 14, color: "#dc3545" }}>{error}</Typography>
            ) : (
              <>
                <ResultCount shown={items.length} />

                {items.length === 0 ? (
                  <Typography sx={{ p: 3, fontSize: 14, color: "#6c757d" }}>
                    Nenhum profissional com os filtros aplicados.
                  </Typography>
                ) : (
                  <Box sx={{ backgroundColor: "#fff", borderRadius: "8px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
                    <Box className="c4u-table__header">
                      {COLUNAS.map((c) => (
                        <Box key={c.titulo} className={`admin-plans-header-cell ${c.classe}`} sx={{ flex: c.flex, minWidth: 0 }}>
                          {c.titulo}
                        </Box>
                      ))}
                    </Box>

                    {items.map((row) => (
                      <Box
                        key={row.ueprId}
                        className="c4u-table__row"
                        onClick={() => setAberto(row)}
                        sx={{ cursor: "pointer" }}
                      >
                        <Box className="admin-plans-cell" data-label="Profissional" sx={{ flex: COLUNAS[0].flex, minWidth: 0, gap: 1 }}>
                          <FotoProfissional userId={row.userId} hasPhoto={row.hasPhoto} nome={row.name} size={32} />
                          <Typography className="admin-plans-name" sx={{ fontSize: 13, overflowWrap: "anywhere" }}>
                            {row.name}
                          </Typography>
                        </Box>
                        <Box className={`admin-plans-cell ${COLUNAS[1].classe}`} data-label="Especialidade" sx={{ flex: COLUNAS[1].flex, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 12.5, color: "#6c757d", overflowWrap: "anywhere" }}>
                            {row.professionalTypeName || "—"}
                          </Typography>
                        </Box>
                        <Box className={`admin-plans-cell ${COLUNAS[2].classe}`} data-label="Documento" sx={{ flex: COLUNAS[2].flex, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 12.5, color: "#6c757d" }}>{row.documentProfessional || "—"}</Typography>
                        </Box>
                        {[row.education, row.experience, row.certification].map((n, i) => (
                          <Box
                            key={COLUNAS[3 + i].titulo}
                            className={`admin-plans-cell ${COLUNAS[3 + i].classe}`}
                            data-label={COLUNAS[3 + i].titulo}
                            sx={{ flex: COLUNAS[3 + i].flex, minWidth: 0 }}
                          >
                            <Typography sx={{ fontSize: 13, fontVariantNumeric: "tabular-nums", color: n > 0 ? "#212529" : "#ced4da" }}>
                              {n}
                            </Typography>
                          </Box>
                        ))}
                        <Box className="admin-plans-cell" data-label="Itens" sx={{ flex: COLUNAS[6].flex, minWidth: 0 }}>
                          {row.total > 0 ? (
                            <Chip
                              size="small"
                              label={row.total}
                              sx={{ height: 22, fontSize: 11.5, fontWeight: 700, backgroundColor: "#e7f5f5", color: "#0d6e6e" }}
                            />
                          ) : (
                            <Tooltip title="Currículo ainda não preenchido" arrow>
                              <Chip size="small" label="—" sx={{ height: 22, fontSize: 11.5, backgroundColor: "#f1f3f5", color: "#adb5bd" }} />
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}
          </div>
        </Container>
      </Box>

      <Dialog
        open={aberto !== null}
        onClose={() => setAberto(null)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        fullScreen={cardEmTelaCheia}
        PaperProps={{ sx: { borderRadius: cardEmTelaCheia ? 0 : "12px" } }}
      >
        {aberto && <CardProfissional row={aberto} onClose={() => setAberto(null)} />}
      </Dialog>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default ReportProfessionalCurriculum;
